import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import moment from "moment-timezone";
import React from "react";
import { t } from "ttag";
import { IQueueSummary } from "../../models/Queues.types";
import useStyles from "./QueuesTable.style";

const STALE_AFTER_MS = 30 * 24 * 60 * 60 * 1000;

interface IQueuesTableProps {
  queues: IQueueSummary[];
  selectedQueue: string | null;
  onSelectQueue: (id: string) => void;
}

const QueuesTable: React.FC<IQueuesTableProps> = ({ queues, selectedQueue, onSelectQueue }) => {
  const classes: any = useStyles();

  const handleRowClick = (id: string) => () => {
    onSelectQueue(id);
  };

  const handleRowKeyDown = (id: string) => (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSelectQueue(id);
    }
  };

  const renderLastJob = (lastJobAt?: number | null) => {
    if (!lastJobAt) {
      return <span className={classes.muted}>—</span>;
    }

    const isStale = Date.now() - lastJobAt > STALE_AFTER_MS;

    return (
      <span className={isStale ? classes.stale : undefined} title={isStale ? t`No job in over 30 days` : undefined}>
        {moment(lastJobAt).format("llll")}
      </span>
    );
  };

  const renderRows = () => {
    if (!queues.length) {
      return (
        <TableRow>
          <TableCell colSpan={9} className={classes.emptyCell}>
            <Typography className={classes.emptyText}>{t`No queues found.`}</Typography>
          </TableCell>
        </TableRow>
      );
    }

    return queues.map((queue) => (
      <TableRow
        hover
        key={queue.id}
        className={`${classes.row} ${queue.id === selectedQueue ? classes.rowSelected : ""}`}
        tabIndex={0}
        aria-selected={queue.id === selectedQueue}
        onClick={handleRowClick(queue.id)}
        onKeyDown={handleRowKeyDown(queue.id)}
      >
        <TableCell className={classes.queueName}>{queue.name}</TableCell>
        <TableCell className={classes.cell}>{renderLastJob(queue.lastJobAt)}</TableCell>
        <TableCell align="right" className={classes.cell}>{queue.counts.waiting || 0}</TableCell>
        <TableCell align="right" className={classes.cell}>{queue.counts.active || 0}</TableCell>
        <TableCell align="right" className={classes.cell}>{queue.counts.delayed || 0}</TableCell>
        <TableCell align="right" className={classes.cell}>{queue.counts.completed || 0}</TableCell>
        <TableCell align="right" className={queue.counts.failed ? classes.failedCount : classes.cell}>
          {queue.counts.failed || 0}
        </TableCell>
        <TableCell align="right" className={classes.cell}>{queue.globalConcurrency ?? "-"}</TableCell>
        <TableCell align="center" className={classes.cell}>
          <span className={queue.paused ? classes.badgePaused : classes.badgeRunning}>
            {queue.paused ? t`Paused` : t`Running`}
          </span>
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <Paper elevation={0} className={classes.tablePaper}>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.headCell}>{t`QUEUE`}</TableCell>
              <TableCell className={classes.headCell}>{t`LAST JOB`}</TableCell>
              <TableCell align="right" className={classes.headCell}>{t`WAITING`}</TableCell>
              <TableCell align="right" className={classes.headCell}>{t`ACTIVE`}</TableCell>
              <TableCell align="right" className={classes.headCell}>{t`DELAYED`}</TableCell>
              <TableCell align="right" className={classes.headCell}>{t`COMPLETED`}</TableCell>
              <TableCell align="right" className={classes.headCell}>{t`FAILED`}</TableCell>
              <TableCell align="right" className={classes.headCell}>{t`CONCURRENCY`}</TableCell>
              <TableCell align="center" className={classes.headCell}>{t`STATE`}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{renderRows()}</TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default QueuesTable;
